import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import BaselineDataService from "../services/BaselineService";

import { debugLog, errorLog } from "../utils/debugUtils";
import { getRejectedError, toRejectedPayload } from "../utils/sliceErrorUtils";

// Exported so tests and preloaded stores can reuse the full slice shape,
// including the structured error/status objects expected by the UI.
export const initialState = {
	list: [],
	selectedBaseline: null,
	selectedBaselineItem: null,
	// Per-operation status & error tracking
	status: {
		retrieve: "idle",
		retrieveOne: "idle",
		create: "idle",
		update: "idle",
		delete: "idle",
		import: "idle",
		copy: "idle",
		find: "idle",
		// Add others as needed
	},
	error: {
		retrieve: null,
		retrieveOne: null,
		create: null,
		update: null,
		delete: null,
		import: null,
		copy: null,
		find: null,
	},
};

// Keep Baseline thunk error handling consistent with Scenario for matching CRUD flows.
export const createBaseline = createAsyncThunk(
	"baselines/create",
	async ({ data }, { rejectWithValue }) => {
		try {
			const res = await BaselineDataService.create(data);
			debugLog(3, "[baselineSlice][FLOW] createBaseline response", { response: res.data });
			return res.data;
		} catch (err) {
			errorLog(4, "[baselineSlice][PROCESS] createBaseline rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "Baseline create failed."));
		}
	}
);

export const retrieveBaselines = createAsyncThunk(
	"baselines/retrieve",
	async (_, { rejectWithValue }) => {
		try {
			const res = await BaselineDataService.getAll();
			debugLog(3, "[baselineSlice][FLOW] retrieveBaselines response", { response: res.data });
			return res.data;
		} catch (err) {
			errorLog(4, "[baselineSlice][PROCESS] retrieveBaselines rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "Baseline retrieve failed."));
		}
	}
);

export const retrieveOneBaseline = createAsyncThunk(
	"baselines/retrieveOne",
	async (id, { rejectWithValue }) => {
		try {
			const res = await BaselineDataService.get(id);
			debugLog(3, "[baselineSlice][FLOW] retrieveOneBaseline response", { response: res.data });

			const transformed = {
				...res.data,
				loadFromDb: true,
			};
			debugLog(4, "[baselineSlice][PROCESS] retrieveOneBaseline transformed", {
				transformed: transformed,
			});
			return transformed;
		} catch (err) {
			errorLog(4, "[baselineSlice][PROCESS] retrieveOneBaseline rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "Baseline retrieve failed."));
		}
	}
);

export const retrieveOneBaselineByUuid = createAsyncThunk(
	"baselines/retrieveOneByUuid",
	async (uuid, { rejectWithValue }) => {
		try {
			const res = await BaselineDataService.findByUuid(uuid);
			debugLog(3, "[baselineSlice][FLOW] retrieveOneBaselineByUuid response", { response: res.data });

			const transformed = {
				...res.data,
				loadFromDb: true,
			};
			debugLog(4, "[baselineSlice][PROCESS] retrieveOneBaselineByUuid transformed", {
				transformed: transformed,
			});
			return transformed;
		} catch (err) {
			errorLog(4, "[baselineSlice][PROCESS] retrieveOneBaselineByUuid rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "Baseline retrieve failed."));
		}
	}
);

export const updateItemThumbnails = createAsyncThunk("baselines/updateItemThumbnails", async (id) => {
	const res = await BaselineDataService.updateItemThumbnails(id);
	debugLog(3, "[baselineSlice][FLOW] updateItemThumbnails response", { response: res.data });

	const transformed = {
		...res.data,
		loadFromDb: true,
	};
	debugLog(4, "[baselineSlice][PROCESS] updateItemThumbnails transformed", {
		transformed: transformed,
	});
	return transformed;
});

export const dumpOneBaseline = createAsyncThunk(
	"baselines/dumpOne",
	async (id, { rejectWithValue }) => {
		try {
			const res = await BaselineDataService.dump(id);
			debugLog(3, "[baselineSlice][FLOW] dumpOneBaseline response", { response: res.data });
			return res.data;
		} catch (err) {
			errorLog(4, "[baselineSlice][PROCESS] dumpOneBaseline rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "Baseline dump failed."));
		}
	}
);

export const updateBaseline = createAsyncThunk(
	"baselines/update",
	async ({ id, data }, { rejectWithValue }) => {
		try {
			const res = await BaselineDataService.update(id, data);
			debugLog(3, "[baselineSlice][FLOW] updateBaseline response", { response: res.data });
			return res.data;
		} catch (err) {
			errorLog(4, "[baselineSlice][PROCESS] updateBaseline rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "Baseline update failed."));
		}
	}
);

export const importBaseline = createAsyncThunk(
	"baselines/import",
	async ({ data }, { rejectWithValue }) => {
		try {
			const res = await BaselineDataService.importBaseline(data);
			debugLog(3, "[baselineSlice][FLOW] importBaseline response", { response: res.data });
			return res.data;
		} catch (err) {
			errorLog(4, "[baselineSlice][PROCESS] importBaseline rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "Baseline import failed."));
		}
	}
);

export const copyBaseline = createAsyncThunk(
	"baselines/copy",
	async ({ data }, { rejectWithValue }) => {
		try {
			const res = await BaselineDataService.copy(data);
			debugLog(3, "[baselineSlice][FLOW] copyBaseline response", { response: res.data });
			return res.data;
		} catch (err) {
			errorLog(4, "[baselineSlice][PROCESS] copyBaseline rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "Baseline copy failed."));
		}
	}
);

export const deleteBaseline = createAsyncThunk(
	"baselines/deleteBaseline",
	async ({ data }, { rejectWithValue }) => {
		try {
			const res = await BaselineDataService.remove(data);
			debugLog(3, "[baselineSlice][FLOW] deleteBaseline response", { response: res.data });
			return res.data;
		} catch (err) {
			errorLog(4, "[baselineSlice][PROCESS] deleteBaseline rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "Baseline delete failed."));
		}
	}
);

export const updateStatus = createAsyncThunk(
	"baselines/updateStatus",
	async ({ id, status_id, updatedBy }, { rejectWithValue }) => {
		try {
			const response = await BaselineDataService.updateStatus(id, status_id, updatedBy);
			debugLog(3, "[baselineSlice][FLOW] updateStatus response", { response: response.data });
			return { id, status_id }; // Minimal return; adjust as needed
		} catch (err) {
			errorLog(4, "[baselineSlice][PROCESS] updateStatus rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "Baseline status update failed."));
		}
	}
);

// Keep search failures aligned with the rest of the slice so UI can show API messages.
export const findBaselineByKeyword = createAsyncThunk(
	"baselines/findByKeyword",
	async ({ keyword }, { rejectWithValue }) => {
		try {
			const res = await BaselineDataService.findByKeyword(keyword);
			debugLog(3, "[baselineSlice][FLOW] findBaselineByKeyword response", { response: res.data });
			return res.data;
		} catch (err) {
			errorLog(4, "[baselineSlice][PROCESS] findBaselineByKeyword rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "Baseline search failed."));
		}
	}
);

export const retrieveOneBaselineItem = createAsyncThunk("baselines/retrieveOneItem", async (id) => {
	const res = await BaselineDataService.getItem(id);
	debugLog(3, "[baselineSlice][FLOW] retrieveOneBaselineItem response", { response: res.data });

	const transformed = {
		...res.data,
		loadFromDb: true,
	};
	debugLog(4, "[baselineSlice][PROCESS] retrieveOneBaselineItem transformed", {
		transformed: transformed,
	});
	return transformed;
});

// Baseline item CRUD powers both Baseline Items and the Scenario Selections
// item-detail modal, so keep these reject payloads on the shared contract.
export const createBaselineItem = createAsyncThunk(
	"baselines/createItem",
	async ({ data }, { rejectWithValue }) => {
		try {
			const res = await BaselineDataService.createItem(data);
			debugLog(3, "[baselineSlice][FLOW] createBaselineItem response", { response: res.data });
			return res.data;
		} catch (err) {
			errorLog(4, "[baselineSlice][PROCESS] createBaselineItem rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "Baseline item create failed."));
		}
	},
);

export const updateBaselineItem = createAsyncThunk(
	"baselines/updateItem",
	async ({ id, data }, { rejectWithValue }) => {
		try {
			const res = await BaselineDataService.updateItem(id, data);
			debugLog(3, "[baselineSlice][FLOW] updateBaselineItem response", { response: res.data });
			return res.data;
		} catch (err) {
			errorLog(4, "[baselineSlice][PROCESS] updateBaselineItem rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "Baseline item update failed."));
		}
	},
);

export const copyBaselineItem = createAsyncThunk(
	"baselines/copyItem",
	async ({ data }, { rejectWithValue }) => {
		try {
			const res = await BaselineDataService.copyItem(data);
			debugLog(3, "[baselineSlice][FLOW] copyBaselineItem response", { response: res.data });
			return res.data;
		} catch (err) {
			errorLog(4, "[baselineSlice][PROCESS] copyBaselineItem rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "Baseline item copy failed."));
		}
	},
);

export const deleteBaselineItem = createAsyncThunk(
	"baselines/deleteItem",
	async ({ data }, { rejectWithValue }) => {
		try {
			const res = await BaselineDataService.deleteItem(data);
			debugLog(3, "[baselineSlice][FLOW] deleteBaselineItem response", { response: res.data });
			return res.data;
		} catch (err) {
			errorLog(4, "[baselineSlice][PROCESS] deleteBaselineItem rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "Baseline item delete failed."));
		}
	},
);

export const updateItemStatus = createAsyncThunk(
	"baselines/updateItemStatus",
	async ({ id, status_id, updatedBy }, { rejectWithValue }) => {
		try {
			const res = await BaselineDataService.updateItemStatus(id, status_id, updatedBy);
			debugLog(3, "[baselineSlice][FLOW] updateItemStatus response", { response: res.data });
			return { id, status_id }; // Minimal return; adjust as needed
		} catch (err) {
			errorLog(4, "[baselineSlice][PROCESS] updateItemStatus rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "Baseline item status update failed."));
		}
	}
);

// Photo mutation thunks use the same rejection contract so upload/remove dialogs
// can show the backend validation message instead of a generic thunk error.
export const uploadItemPhoto = createAsyncThunk(
	"baselines/uploadItemPhoto",
	async ({ id, file }, { rejectWithValue }) => {
		try {
			const res = await BaselineDataService.uploadItemPhoto(id, file);
			debugLog(3, "[baselineSlice][FLOW] uploadItemPhoto response", { response: res.data });
			return res.data;
		} catch (err) {
			errorLog(4, "[baselineSlice][PROCESS] uploadItemPhoto rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "Baseline item photo upload failed."));
		}
	}
);

export const removeItemPhoto = createAsyncThunk(
	"baselines/removeItemPhoto",
	async (id, { rejectWithValue }) => {
		try {
			const res = await BaselineDataService.removeItemPhoto(id);
			debugLog(3, "[baselineSlice][FLOW] removeItemPhoto response", { response: res.data });
			return res.data;
		} catch (err) {
			errorLog(4, "[baselineSlice][PROCESS] removeItemPhoto rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "Baseline item photo removal failed."));
		}
	}
);

// The slice
const baselineSlice = createSlice({
	name: "baselines",
	initialState,
	reducers: {
		setSelectedBaseline: (state, action) => {
			state.selectedBaseline = action.payload ?? null;
			debugLog(4, "[baselineSlice][PROCESS] setSelectedBaseline", { payload: action.payload });
		},
		clearSelectedBaseline: (state) => {
			state.selectedBaseline = null;
			debugLog(4, "[baselineSlice][PROCESS] clearSelectedBaseline", {});
		},
		setSelectedBaselineItem: (state, action) => {
			state.selectedBaselineItem = action.payload ?? null;
			debugLog(4, "[baselineSlice][PROCESS] setSelectedBaselineItem", { payload: action.payload });
		},
		clearSelectedBaselineItem: (state) => {
			state.selectedBaselineItem = null;
			debugLog(4, "[baselineSlice][PROCESS] clearSelectedBaselineItem", {});
		},
		resetError: (state, action) => {
			if (action.payload && action.payload.type) {
				state.error[action.payload.type] = null;
				debugLog(4, "[baselineSlice][PROCESS] resetError one", { type: action.payload.type });
			} else {
				Object.keys(state.error).forEach((key) => {
					state.error[key] = null;
				});
				debugLog(4, "[baselineSlice][PROCESS] resetError all", {});
			}
		},
	},
	extraReducers: (builder) => {
		// --- [retrieveBaselines] ---
		builder
			.addCase(retrieveBaselines.pending, (state) => {
				state.status.retrieve = "loading";
				state.error.retrieve = null;
			})
			.addCase(retrieveBaselines.fulfilled, (state, action) => {
				state.status.retrieve = "succeeded";
				state.list = action.payload;
			})
			.addCase(retrieveBaselines.rejected, (state, action) => {
				state.status.retrieve = "failed";
				// Keep retrieval failures structured so Baseline list/detail screens
				// can reuse the same dialog contract as User/UserGroup.
				state.error.retrieve = getRejectedError(action, "Baseline retrieve failed.");
				debugLog(4, "[baselineSlice][PROCESS] retrieveBaselines rejected", { error: action.error });
			})

			// --- [retrieveOneBaseline] ---
			.addCase(retrieveOneBaseline.pending, (state) => {
				state.status.retrieveOne = "loading";
				state.error.retrieveOne = null;
			})
			.addCase(retrieveOneBaseline.fulfilled, (state, action) => {
				state.status.retrieveOne = "succeeded";
				state.selectedBaseline = action.payload;
			})
			.addCase(retrieveOneBaseline.rejected, (state, action) => {
				state.status.retrieveOne = "failed";
				state.error.retrieveOne = getRejectedError(action, "Baseline retrieve failed.");
				debugLog(4, "[baselineSlice][PROCESS] retrieveOneBaseline rejected", { error: action.error });
			})

			// --- [retrieveOneBaselineByUuid] ---
			.addCase(retrieveOneBaselineByUuid.pending, (state) => {
				state.status.retrieveOne = "loading";
				state.error.retrieveOne = null;
			})
			.addCase(retrieveOneBaselineByUuid.fulfilled, (state, action) => {
				state.status.retrieveOne = "succeeded";
				state.selectedBaseline = action.payload;
			})
			.addCase(retrieveOneBaselineByUuid.rejected, (state, action) => {
				state.status.retrieveOne = "failed";
				state.error.retrieveOne = getRejectedError(action, "Baseline retrieve failed.");
				debugLog(4, "[baselineSlice][PROCESS] retrieveOneBaselineByUuid rejected", {
					error: action.error,
				});
			})

			// --- [createBaseline] ---
			.addCase(createBaseline.pending, (state) => {
				state.status.create = "loading";
				state.error.create = null;
			})
			.addCase(createBaseline.fulfilled, (state, action) => {
				state.status.create = "succeeded";
				state.list.push(action.payload);
			})
			.addCase(createBaseline.rejected, (state, action) => {
				state.status.create = "failed";
				// Keep mutation failures structured so Baseline create/update dialogs
				// can preserve backend validation messages instead of generic strings.
				state.error.create = getRejectedError(action, "Baseline create failed.");
				debugLog(4, "[baselineSlice][PROCESS] createBaseline rejected", { error: action.error });
			})

			// --- [updateBaseline] ---
			.addCase(updateBaseline.pending, (state) => {
				state.status.update = "loading";
				state.error.update = null;
			})
			.addCase(updateBaseline.fulfilled, (state, action) => {
				state.status.update = "succeeded";
				const idx = state.list.findIndex((b) => b.id === action.payload.id);
				if (idx !== -1) state.list[idx] = action.payload;
				if (state.selectedBaseline && state.selectedBaseline.id === action.payload.id) {
					state.selectedBaseline = action.payload;
				}

				debugLog(4, "[baselineSlice][PROCESS] updateBaseline fulfilled updated baseline", {
					payload: action.payload,
				});
			})
			.addCase(updateBaseline.rejected, (state, action) => {
				state.status.update = "failed";
				// Preserve the normalized object so update dialogs can distinguish
				// backend validation failures from transport/timeout fallbacks.
				state.error.update = getRejectedError(action, "Baseline update failed.");
				debugLog(4, "[baselineSlice][PROCESS] updateBaseline rejected", { error: action.error });
			})

			// --- [deleteBaseline] ---
			.addCase(deleteBaseline.pending, (state) => {
				state.status.delete = "loading";
				state.error.delete = null;
			})
			.addCase(deleteBaseline.fulfilled, (state, action) => {
				state.status.delete = "succeeded";
				const deletedId =
					typeof action.payload === "number"
						? action.payload
						: action.payload?.id ?? action.meta?.arg?.data?.id;
				if (deletedId == null) return;

				state.list = state.list.filter((b) => b.id !== deletedId);
				if (state.selectedBaseline && state.selectedBaseline.id === deletedId) {
					state.selectedBaseline = null;
				}
			})
			.addCase(deleteBaseline.rejected, (state, action) => {
				state.status.delete = "failed";
				// Keep delete failures structured so dialogs can preserve backend
				// messages instead of collapsing everything to one generic string.
				state.error.delete = getRejectedError(action, "Baseline delete failed.");
				debugLog(4, "[baselineSlice][PROCESS] deleteBaseline rejected", { error: action.error });
			})

			// --- [importBaseline] ---
			.addCase(importBaseline.pending, (state) => {
				state.status.import = "loading";
				state.error.import = null;
			})
			.addCase(importBaseline.fulfilled, (state, action) => {
				state.status.import = "succeeded";
				state.list.push(action.payload);
			})
			.addCase(importBaseline.rejected, (state, action) => {
				state.status.import = "failed";
				// Preserve the normalized object so import dialogs can distinguish
				// backend validation failures from transport/timeout fallbacks.
				state.error.import = getRejectedError(action, "Baseline import failed.");
				debugLog(4, "[baselineSlice][PROCESS] importBaseline rejected", { error: action.error });
			})

			// --- [copyBaseline] ---
			.addCase(copyBaseline.pending, (state) => {
				state.status.copy = "loading";
				state.error.copy = null;
			})
			.addCase(copyBaseline.fulfilled, (state, action) => {
				state.status.copy = "succeeded";
				const copiedBaseline =
					action.payload && typeof action.payload === "object" && action.payload.id != null
						? action.payload
						: action.payload?.baseline && action.payload.baseline.id != null
							? action.payload.baseline
							: null;

				if (copiedBaseline) {
					state.list.push(copiedBaseline);
				}
			})
			.addCase(copyBaseline.rejected, (state, action) => {
				state.status.copy = "failed";
				state.error.copy = getRejectedError(action, "Baseline copy failed.");
				debugLog(4, "[baselineSlice][PROCESS] copyBaseline rejected", { error: action.error });
			})

			// --- [findBaselineByKeyword] ---
			.addCase(findBaselineByKeyword.pending, (state) => {
				state.status.find = "loading";
				state.error.find = null;
			})
			.addCase(findBaselineByKeyword.fulfilled, (state, action) => {
				state.status.find = "succeeded";
				state.list = action.payload;
			})
			.addCase(findBaselineByKeyword.rejected, (state, action) => {
				state.status.find = "failed";
				// Keep Baseline search failures on the shared normalized contract so
				// future search UI can surface backend, transport, and timeout cases
				// the same way as the rest of the stable-module flows.
				state.error.find = getRejectedError(action, "Baseline search failed.");
				debugLog(4, "[baselineSlice][PROCESS] findBaselineByKeyword rejected", {
					error: action.error,
				});
			})

			.addCase(updateStatus.pending, (state) => {
				state.status.update = "loading"; // or 'updateStatus' if you want it separated
				state.error.update = null;
			})
			.addCase(updateStatus.fulfilled, (state, action) => {
				state.status.update = "succeeded";
				// Merge the returned status change into any existing record so we do
				// not drop the rest of the Baseline fields on a successful archive/restore.
				const idx = state.list.findIndex((b) => b.id === action.payload.id);
				if (idx !== -1) {
					state.list[idx] = {
						...state.list[idx],
						...action.payload,
					};
				}
				if (state.selectedBaseline && state.selectedBaseline.id === action.payload.id) {
					state.selectedBaseline = {
						...state.selectedBaseline,
						...action.payload,
					};
				}
			})
			.addCase(updateStatus.rejected, (state, action) => {
				state.status.update = "failed";
				// Status updates still reuse the shared `update` error bucket, so
				// keep the normalized error object here for consistent dialog rendering.
				state.error.update = getRejectedError(action, "Baseline status update failed.");
				debugLog(4, "[baselineSlice][PROCESS] updateStatus rejected", { error: action.error });
			})

			// retrieveOneBaselineItem
			.addCase(retrieveOneBaselineItem.fulfilled, (state, action) => {
				state.selectedBaselineItem = action.payload;
				debugLog(4, "[baselineSlice][PROCESS] retrieveOneBaselineItem fulfilled", {
					payload: action.payload,
				});
			});

		// You can keep expanding for other thunks as needed!
	},
});

// --- END baselineSlice ---

const { reducer } = baselineSlice;
export default reducer;

// Export action creators for UI
export const {
	setSelectedBaseline,
	clearSelectedBaseline,
	resetError,
	setSelectedBaselineItem,
	clearSelectedBaselineItem,
} = baselineSlice.actions;
